import { useQuery } from '@tanstack/react-query';
import { useImpersonatedAuth } from './useImpersonatedAuth';
import { api } from '../services/api';

interface ComponentAccessMap {
  [component: string]: 'none' | 'view' | 'edit' | 'manage' | 'restricted';
}

export const useComponentAccess = () => {
  const { user, isImpersonating } = useImpersonatedAuth();

  const { data: componentAccess, isLoading } = useQuery({
    queryKey: ['component-access', user?.id, isImpersonating],
    queryFn: async () => {
      if (!user?.id) return {};

      if (user?.is_system_admin) {
        return {};
      }

      try {
        const response = await api.get(`/component-access/${user.id}`);
        return (response.data.data || {}) as ComponentAccessMap;
      } catch {
        console.warn('Failed to fetch component access, using default permissions');
        return {};
      }
    },
    enabled: !!user?.id && !user?.is_system_admin,
    staleTime: 5 * 60 * 1000,
  });

  // Raw effective access level for a component path (maps the path to its access
  // token the same way hasAccess does). Distinguishes 'restricted' from 'view'+,
  // which the boolean hasAccess() collapses — needed where an explicit
  // 'restricted' grant must be treated differently from a full grant (e.g. Order
  // Requests creator-only scoping).
  const getAccessLevel = (componentPath: string): 'none' | 'view' | 'edit' | 'manage' | 'restricted' => {
    if (!user) return 'none';
    if (user?.is_system_admin) return 'manage';
    if (isLoading || !componentAccess) return 'none';
    return componentAccess[mapComponentToAccessToken(componentPath)] || 'none';
  };

  const hasAccess = (componentPath: string, requiredAccess: 'view' | 'edit' | 'manage' | 'restricted' = 'view'): boolean => {
    if (!user) {
      return false;
    }

    if (user?.is_system_admin) {
      return true;
    }

    if (isLoading) {
      return false;
    }

    if (!componentAccess || Object.keys(componentAccess).length === 0) {
      return false;
    }

    const mappedComponent = mapComponentToAccessToken(componentPath);
    const accessLevel = componentAccess[mappedComponent] || 'none';

    if (accessLevel === 'none') return false;

    // Check specific access level requirements
    if (requiredAccess === 'manage') return accessLevel === 'manage';
    if (requiredAccess === 'edit') return accessLevel === 'edit' || accessLevel === 'manage';

    // For 'view' or 'restricted', basic access is enough (which we checked with !== 'none')
    return true;
  };

  const canEdit = (componentPath: string): boolean => {
    if (!user) {
      return false;
    }

    if (user?.is_system_admin) {
      return true;
    }

    if (!componentAccess || Object.keys(componentAccess).length === 0) {
      return false;
    }

    const mappedComponent = mapComponentToAccessToken(componentPath);
    const accessLevel = componentAccess[mappedComponent];
    return accessLevel === 'edit' || accessLevel === 'manage';
  };

  const canManage = (componentPath: string): boolean => {
    if (!user) {
      return false;
    }

    if (user?.is_system_admin) {
      return true;
    }

    if (!componentAccess || Object.keys(componentAccess).length === 0) {
      return false;
    }

    const mappedComponent = mapComponentToAccessToken(componentPath);
    const accessLevel = componentAccess[mappedComponent];
    return accessLevel === 'manage';
  };

  // True when the user's CRM access is the 'restricted' (salesperson) role.
  // Used to grant scoped access to features like Order Requests that restricted
  // salespeople reach via their client assignments rather than a direct grant.
  const isCrmRestricted = !user?.is_system_admin && componentAccess?.['crm'] === 'restricted';

  const canManageAnyComponent = (): boolean => {
    if (!user) return false;
    if (user?.is_system_admin) return true;
    if (!componentAccess || Object.keys(componentAccess).length === 0) return false;

    for (const [, value] of Object.entries(componentAccess)) {
      if (value === 'manage') {
        return true;
      }
    }
    return false;
  };

  const filterMenuItems = (items: any[]): any[] => {
    return items.filter(item => {
      if ('path' in item) {
        const componentName = getComponentName(item.path);
        return hasAccess(componentName);
      }
      if ('items' in item) {
        const filteredItems = filterMenuItems(item.items);
        return filteredItems.length > 0;
      }
      return true;
    });
  };

  return {
    componentAccess,
    isLoading,
    hasAccess,
    getAccessLevel,
    canEdit,
    canManage,
    canManageAnyComponent,
    isCrmRestricted,
    filterMenuItems,
  };
};

const mapComponentToAccessToken = (componentPath: string): string => {
  const crmSubcomponents = ['projects', 'tasks', 'tickets', 'clients', 'project-templates', 'task-templates', 'documents'];

  if (crmSubcomponents.includes(componentPath)) {
    return 'crm';
  }

  return componentPath;
};

const getComponentName = (path: string): string => {
  if (path.startsWith('/')) {
    return path.slice(1).split('/')[0];
  }
  return path;
};
